import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { LocationPicker } from "@/components/LocationPicker";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import type { Shelter } from "@/types/shelter";

const TRACKED_FIELDS = [
  "name", "city", "region", "address", "postal_code", "lat", "lng",
  "phone", "email", "website", "current_needs", "urgent_needs",
  "dog_walking_available", "volunteering_available", "dogs_available",
  "cats_available", "volunteer_info", "description",
] as const;

// Map shelter field names to shelter_updates field names where they differ
const fieldToUpdateKey = (key: string) => key === "name" ? "shelter_name" : key;

const useUpdateShelter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shelter: Partial<Shelter> & { id: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const { id, ...updateData } = shelter;
      const { created_at, last_updated, urgent_needs_set_at, organization_verified_at, organization_verification_requested_at, ...fields } = updateData as any;

      // Fetch current state to detect changes
      const { data: current, error: fetchErr } = await supabase
        .from("shelters")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchErr || !current) throw new Error("Nie udało się pobrać aktualnych danych");

      // Detect changed fields
      const normalize = (v: unknown) => (v === null || v === undefined || v === "") ? null : v;
      const originalValues: Record<string, unknown> = {};
      const changedUpdateRecord: Record<string, unknown> = {};

      for (const key of TRACKED_FIELDS) {
        const oldVal = normalize(current[key]);
        const newVal = normalize(fields[key]);
        const updateKey = fieldToUpdateKey(key);

        if (oldVal !== newVal) {
          originalValues[key === "name" ? "name" : key] = current[key];
          changedUpdateRecord[updateKey] = fields[key];
        } else {
          changedUpdateRecord[updateKey] = current[key];
        }
      }

      // Update the shelter
      const { error } = await supabase
        .from("shelters")
        .update({ ...fields, last_updated: new Date().toISOString() })
        .eq("id", id);

      if (error) throw new Error(error.message);

      // If anything changed, create a history entry
      if (Object.keys(originalValues).length > 0) {
        await supabase.from("shelter_updates").insert({
          shelter_id: id,
          shelter_name: fields.name || current.name,
          city: fields.city || current.city,
          region: fields.region ?? current.region,
          address: fields.address ?? current.address,
          postal_code: fields.postal_code ?? current.postal_code,
          lat: fields.lat ?? current.lat,
          lng: fields.lng ?? current.lng,
          phone: fields.phone ?? current.phone,
          email: fields.email ?? current.email,
          website: fields.website ?? current.website,
          current_needs: fields.current_needs ?? current.current_needs,
          urgent_needs: fields.urgent_needs ?? current.urgent_needs,
          dog_walking_available: fields.dog_walking_available ?? current.dog_walking_available,
          volunteering_available: fields.volunteering_available ?? current.volunteering_available,
          dogs_available: fields.dogs_available ?? current.dogs_available,
          cats_available: fields.cats_available ?? current.cats_available,
          volunteer_info: fields.volunteer_info ?? current.volunteer_info,
          description: fields.description ?? current.description,
          original_values: originalValues,
          status: "approved",
          source: "admin_edit",
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        } as any);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelters-for-verification"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-updates"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-history"] });
    },
  });
};
interface ShelterEditDialogProps {
  shelter: Shelter | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShelterEditDialog = ({ shelter, open, onOpenChange }: ShelterEditDialogProps) => {
  const updateShelter = useUpdateShelter();
  const [form, setForm] = useState<Partial<Shelter>>({});
  const [cityInput, setCityInput] = useState("");
  const [addressInput, setAddressInput] = useState("");

  const { reverseGeocode } = useReverseGeocode();

  useEffect(() => {
    if (shelter) {
      setForm({ ...shelter });
      setCityInput(shelter.city || "");
      setAddressInput(shelter.address || "");
    }
  }, [shelter]);

  if (!shelter) return null;

  const set = (key: keyof Shelter, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    try {
      await updateShelter.mutateAsync({ id: shelter.id, ...form });
      toast.success("Dane schroniska zaktualizowane");
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Błąd zapisu");
    }
  };

  const handleCitySuggestion = (suggestion: any) => {
    setCityInput(suggestion.city || suggestion.display);
    set("city", suggestion.city || suggestion.display);
    if (suggestion.region) set("region", suggestion.region);
    if (suggestion.lat && suggestion.lng) {
      set("lat", suggestion.lat);
      set("lng", suggestion.lng);
    }
  };

  const handleAddressSuggestion = (suggestion: any) => {
    setAddressInput(suggestion.address || suggestion.display);
    if (suggestion.address) set("address", suggestion.address);
    if (suggestion.city) {
      set("city", suggestion.city);
      setCityInput(suggestion.city);
    }
    if (suggestion.region) set("region", suggestion.region);
    if (suggestion.postalCode) set("postal_code", suggestion.postalCode);
    if (suggestion.lat && suggestion.lng) {
      set("lat", suggestion.lat);
      set("lng", suggestion.lng);
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    set("lat", lat);
    set("lng", lng);
  };

  const handleReverseGeocode = async (lat: number, lng: number) => {
    const result = await reverseGeocode(lat, lng);
    if (result) {
      if (result.city) {
        set("city", result.city);
        setCityInput(result.city);
      }
      if (result.address) {
        set("address", result.address);
        setAddressInput(result.address);
      }
      if (result.region) set("region", result.region);
      if (result.postalCode) set("postal_code", result.postalCode);
    }
  };

  const booleanField = (key: keyof Shelter, label: string) => (
    <div className="flex items-center justify-between">
      <Label>{label}</Label>
      <select
        className="text-sm border rounded px-2 py-1 bg-background"
        value={form[key] === null ? "null" : form[key] ? "true" : "false"}
        onChange={(e) => {
          const v = e.target.value;
          set(key, v === "null" ? null : v === "true");
        }}
      >
        <option value="true">Tak</option>
        <option value="false">Nie</option>
        <option value="null">Brak informacji</option>
      </select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edytuj: {shelter.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Nazwa</Label>
              <Input value={form.name || ""} onChange={e => set("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Miasto</Label>
              <AddressAutocomplete
                id="edit-city"
                value={cityInput}
                onChange={(v) => { setCityInput(v); set("city", v); }}
                onSuggestionSelect={handleCitySuggestion}
                placeholder="Wpisz miasto..."
                type="city"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Region</Label>
              <Input value={form.region || ""} onChange={e => set("region", e.target.value || null)} />
            </div>
            <div className="space-y-1.5">
              <Label>Adres</Label>
              <AddressAutocomplete
                id="edit-address"
                value={addressInput}
                onChange={(v) => { setAddressInput(v); set("address", v || null); }}
                onSuggestionSelect={handleAddressSuggestion}
                placeholder="Wpisz adres..."
                type="address"
                filterByCity={form.city || ""}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Kod pocztowy</Label>
              <Input value={form.postal_code || ""} onChange={e => set("postal_code", e.target.value || null)} />
            </div>
          </div>

          {/* Map */}
          <LocationPicker
            lat={form.lat ?? null}
            lng={form.lng ?? null}
            onLocationSelect={handleLocationSelect}
            onReverseGeocode={handleReverseGeocode}
          />

          {/* Contact */}
          <div className="border-t pt-4">
            <h3 className="font-medium mb-3">Kontakt</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <Input value={form.phone || ""} onChange={e => set("phone", e.target.value || null)} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={form.email || ""} onChange={e => set("email", e.target.value || null)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Strona www</Label>
                <Input value={form.website || ""} onChange={e => set("website", e.target.value || null)} />
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="border-t pt-4 space-y-3">
            <h3 className="font-medium">Statusy</h3>
            {booleanField("urgent_needs", "Pilne potrzeby")}
            {booleanField("dog_walking_available", "Spacery z psami")}
            {booleanField("volunteering_available", "Wolontariat")}
            {booleanField("dogs_available", "Psy do adopcji")}
            {booleanField("cats_available", "Koty do adopcji")}
          </div>

          {/* Text fields */}
          <div className="border-t pt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Aktualne potrzeby</Label>
              <Textarea rows={3} value={form.current_needs || ""} onChange={e => set("current_needs", e.target.value || null)} />
            </div>
            <div className="space-y-1.5">
              <Label>Informacje o wolontariacie</Label>
              <Textarea rows={3} value={form.volunteer_info || ""} onChange={e => set("volunteer_info", e.target.value || null)} />
            </div>
            <div className="space-y-1.5">
              <Label>Opis</Label>
              <Textarea rows={3} value={form.description || ""} onChange={e => set("description", e.target.value || null)} />
            </div>
          </div>

          {/* Verification flags */}
          <div className="border-t pt-4 space-y-3">
            <h3 className="font-medium">Weryfikacja</h3>
            <div className="flex items-center justify-between">
              <Label>Dane zweryfikowane</Label>
              <Switch checked={form.data_verified ?? false} onCheckedChange={v => set("data_verified", v)} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Skontaktowano</Label>
              <Switch checked={form.contacted ?? false} onCheckedChange={v => set("contacted", v)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={updateShelter.isPending}>
              {updateShelter.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Zapisz zmiany
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
